import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BriefcaseBusiness, FileText, FolderGit2, BadgeCheck, Youtube, Trophy,
} from "lucide-react";
import {
  AppBadge, AppDataTable, Callout, FilterToolbar, PageHeader, SearchInput,
  StatsCard, StatsGrid, UserAvatar,
} from "../ui";
import { ScorePill } from "./ResumeReport";
import { SENIORITY_LEVELS, fmtDate, labelFor } from "./constants";
import { fetchPortfolioList, errorText } from "./portfolioApi";

/**
 * PortfoliosPage — the placement view: who is job-ready, and who is not started.
 *
 * ⚠️ SERVER-PAGINATED, SERVER-SEARCHED. An institute has thousands of students
 * and a portfolio row carries arrays; pulling the lot to filter in the browser
 * is the shape that works on the demo tenant and times out on the real one. So
 * search and paging go upstream and the table gets exactly the page it shows.
 *
 * ⚠️ The resume score column is ADVICE, not an assessment. It is a language
 * model's read of a PDF and it is here to tell a counsellor who to sit down
 * with, never to rank or shortlist. That is why the column sorts by nothing and
 * the page offers no "top candidates" view — see models/ResumeAnalysis.js.
 *
 * Instructors reach this page too, and the server narrows their rows to the
 * students in their own batches (utils/portfolioAccess). Nothing here filters
 * by role; the list simply arrives shorter.
 */

const PAGE_SIZE = 20;

/** A count with its icon, for the four small evidence columns. */
const Count = ({ icon: Icon, value }) => (
  <span
    className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-tabular-nums ${
      value > 0 ? "tw-text-content" : "tw-text-content-subtle"
    }`}
  >
    <Icon size={13} /> {value || 0}
  </span>
);

const PortfoliosPage = ({ basePath = "/students" }) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Debounced so typing a name is one request, not one per keystroke.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPortfolioList({ page, pageSize: PAGE_SIZE, search: query });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(errorText(err, "Failed to load portfolios."));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { load(); }, [load]);

  // Counted over the page on screen, and labelled as such — an institute-wide
  // number would need its own endpoint, and a page-scoped figure presented as a
  // total is the kind of stat that ends up in a board deck.
  const stats = useMemo(() => ({
    withResume: rows.filter((r) => r.hasResume).length,
    analysed: rows.filter((r) => r.resumeScore !== null && r.resumeScore !== undefined).length,
    withProjects: rows.filter((r) => r.projectCount > 0).length,
  }), [rows]);

  const columns = [
    {
      key: "student",
      label: "Student",
      render: (r) => (
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <UserAvatar name={r.student?.name || "?"} size="sm" />
          <div className="tw-min-w-0">
            <div className="tw-truncate tw-text-[0.875rem] tw-font-medium tw-text-content">
              {r.student?.name || "Unknown"}
            </div>
            <div className="tw-truncate tw-text-xs tw-text-content-subtle">
              {r.student?.email || r.student?.mobile || ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "targetRole",
      label: "Target role",
      render: (r) => (
        r.targetRole
          ? (
            <div className="tw-min-w-0">
              <div className="tw-truncate tw-text-[0.85rem] tw-text-content">{r.targetRole}</div>
              {r.seniority && (
                <div className="tw-text-xs tw-text-content-subtle">
                  {labelFor(SENIORITY_LEVELS, r.seniority)}
                </div>
              )}
            </div>
          )
          : <span className="tw-text-sm tw-text-content-subtle">Not set</span>
      ),
    },
    {
      key: "resume",
      label: "Resume",
      align: "center",
      render: (r) => (
        !r.hasResume
          ? <AppBadge variant="secondary" size="sm">None</AppBadge>
          : <ScorePill score={r.resumeScore} size="sm" />
      ),
    },
    {
      key: "evidence",
      label: "Evidence",
      render: (r) => (
        <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1">
          <Count icon={FolderGit2} value={r.projectCount} />
          <Count icon={BadgeCheck} value={r.certificationCount} />
          <Count icon={Trophy} value={r.achievementCount} />
          <Count icon={Youtube} value={r.videoCount} />
        </div>
      ),
    },
    {
      key: "updatedAt",
      label: "Last updated",
      render: (r) => (
        <span className="tw-text-xs tw-text-content-muted">{fmtDate(r.updatedAt)}</span>
      ),
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        title="Student Portfolios"
        description="Resumes, projects and credentials your students have published to their profile."
        icon={BriefcaseBusiness}
      />

      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      <StatsGrid>
        <StatsCard title="Portfolios on this page" value={rows.length} icon={BriefcaseBusiness} color="primary" />
        <StatsCard title="Resume uploaded" value={stats.withResume} icon={FileText} color="blue" />
        <StatsCard title="Resume analysed" value={stats.analysed} icon={BadgeCheck} color="emerald" />
        <StatsCard title="Has projects" value={stats.withProjects} icon={FolderGit2} color="violet" />
      </StatsGrid>

      <AppDataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`${basePath}/${r.student?._id}/portfolio`)}
        loading={loading}
        fill={false}
        emptyMessage={
          query
            ? `No portfolios match "${query}".`
            : "No student has started a portfolio yet. They build one from their own Account page."
        }
        toolbar={
          <FilterToolbar
            flush
            search={
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by name, email or mobile…"
              />
            }
          />
        }
        pagination={{
          page,
          total,
          pageSize: PAGE_SIZE,
          totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
          onChange: setPage,
        }}
      />
    </div>
  );
};

export default PortfoliosPage;
